import React, { useState, useEffect, useCallback } from 'react';
import { signInWithEmailAndPassword, signOut } from '../staffAuthApi';
import { mainAuth as auth } from '../appAuth';
import { sendCustomVerificationEmail } from '../api/emailVerificationApi';
import { VerifyEmailView } from './VerifyEmailView';
import './VerifyEmailModal.css';
import './VerifyEmailView.css';

/**
 * Modal shown on Login when the user is not verified.
 * Uses HTML/CSS only (no MUI). Resend flow: sign in → send verification email → sign out → notify & close.
 */
export function VerifyEmailModal({ open, email, password, onClose, onNotification, onCooldownStart, formatError }) {
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState('Sending verification email…');
  const [message, setMessage] = useState('Please verify your email before logging in. Check your inbox for the verification link.');
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return;
    const t = setInterval(() => setResendCooldownSeconds((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldownSeconds]);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setResendLoading(false);
      setResendCooldownSeconds(0);
      setMessage('Please verify your email before logging in. Check your inbox for the verification link.');
    }
  }, [open]);

  const handleClose = useCallback(() => {
    if (!loading) {
      if (resendCooldownSeconds > 0) onCooldownStart?.(resendCooldownSeconds);
      onClose();
    }
  }, [loading, resendCooldownSeconds, onClose, onCooldownStart]);

  const handleResendVerification = useCallback(async () => {
    try {
      setResendLoading(true);
      setLoading(true);
      setLoadingLabel('Signing in…');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      setLoadingLabel('Sending verification email…');
      const idToken = await user.getIdToken();
      await sendCustomVerificationEmail(idToken);
      setLoading(false);
      setResendLoading(false);
      onCooldownStart?.(120);
      onNotification('Verification email sent. Please check your inbox.', 'success');
      onClose();
      signOut(auth).catch(() => {}); // sign out in background so we don't block the success UI
    } catch (error) {
      console.error('Error sending verification email:', error);
      setLoading(false);
      setResendLoading(false);
      const cooldown = error?.retryAfterSeconds;
      if (typeof cooldown === 'number' && cooldown > 0) {
        setResendCooldownSeconds(cooldown);
        onCooldownStart?.(cooldown);
        setMessage(`Please wait ${cooldown} seconds before requesting another verification email.`);
      } else {
        setMessage(`Failed to send verification email: ${formatError(error)}`);
      }
    }
  }, [email, password, onClose, onNotification, onCooldownStart, formatError]);

  if (!open) return null;

  return (
    <div
      className="verify-email-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="verify-email-modal-title"
      aria-hidden={!open}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="verify-email-modal" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="verify-email-modal__loading">
            <div className="verify-email-modal__spinner" aria-hidden="true" />
            <p className="verify-email-modal__loading-text">Sending verification email</p>
            <p className="verify-email-modal__loading-sub">{loadingLabel}</p>
          </div>
        ) : (
          <VerifyEmailView
            state="prompt"
            title="Email verification required"
            copy={message}
            resendCooldownSeconds={resendCooldownSeconds}
            resendLoading={resendLoading}
            onResend={handleResendVerification}
            secondaryActionLabel="Back to login"
            onSecondaryAction={handleClose}
          />
        )}
      </div>
    </div>
  );
}

export default VerifyEmailModal;
